import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { Header, Table, Checkbox, Icon } from 'semantic-ui-react';
import Fade from 'react-reveal/Fade';
import { FundComparison, ETFAgent as ETFActions } from 'actions';
import { LogoSpinner, ButtonFilled } from 'components';
import { colors } from 'utils/colors';
import { translation as translationTools } from '../../Translation/tools';

class Category extends Component {
  state = {
    searchInput: '',
    hasSearched: false,
    sortBy: { property: null, ascending: false },
  };

  setIsByCategory = (value) => {
    const { dispatch } = this.props;
    dispatch(ETFActions.updateField('selectedTickers', []));
    dispatch(ETFActions.updateField('isByCategory', value));
  };

  setSort = (property) => {
    this.setState({
      sortBy: { property, ascending: !this.state.sortBy.ascending },
    });
  };

  goToFundComparison = () => {
    const { dispatch, etfData, history } = this.props;
    const { selectedTickers } = etfData;

    dispatch(FundComparison.clear());

    Promise.all(
      selectedTickers.map((ticker) => dispatch(FundComparison.addFund(ticker)))
    ).then(() => {
      window.scrollTo(0, 0);
      history.push('/tools/fund-comparison');
    });
  };

  toggleTicker = (ticker) => {
    const { dispatch, etfData } = this.props;
    const { selectedTickers } = etfData;

    if (selectedTickers.includes(ticker)) {
      dispatch(ETFActions.removeSelectedTicker(ticker));
    } else if (selectedTickers.length < 4) {
      dispatch(ETFActions.addSelectedTicker(ticker));
    }
  };

  handleSearchChange = (e) => {
    this.setState({
      searchInput: e.target.value,
    });
  };

  handleSearch = (query = this.state.searchInput) => {
    const { dispatch } = this.props;

    if (!query.trim()) return;

    this.setState({
      searchInput: query,
      hasSearched: true,
    });

    dispatch(ETFActions.categorySearch(query));
  };

  handleQueryClick = (query) => {
    this.handleSearch(query);
  };

  sortResults = (property, asc, results) => {
    if (!property || !results) return results;

    const numericProps = ['aum', 'mer', '_yield', 'volume'];
    const copy = results.slice();

    copy.sort((a, b) => {
      if (numericProps.includes(property)) {
        const aVal = a[property];
        const bVal = b[property];
        const aIsNull = aVal === null || aVal === undefined || aVal === '';
        const bIsNull = bVal === null || bVal === undefined || bVal === '';

        if (aIsNull && bIsNull) return 0;
        if (aIsNull) return 1;
        if (bIsNull) return -1;

        return asc ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
      }

      const aText = (
        (a[property] && a[property].toString()) ||
        ''
      ).toUpperCase();
      const bText = (
        (b[property] && b[property].toString()) ||
        ''
      ).toUpperCase();

      return asc ? aText.localeCompare(bText) : bText.localeCompare(aText);
    });

    return copy;
  };

  formatAum = (value) => {
    if (value === null || value === undefined || value === '') return '-';

    const num = Number(value);
    if (Number.isNaN(num)) return '-';

    const abs = Math.abs(num);

    if (abs >= 1e9) {
      return `$${(num / 1e9).toFixed(2)}B`;
    }
    if (abs >= 1e6) {
      return `$${(num / 1e6).toFixed(2)}M`;
    }
    return `$${num.toLocaleString()}`;
  };

  renderResultsTable = () => {
    const { etfData, french } = this.props;
    const language = french ? 'fr' : 'en';
    const { categoryResults, selectedTickers } = etfData;
    const { sortBy } = this.state;
    if (!categoryResults) return null;
    return (
      <div style={{ marginTop: '20px' }}>
        <Table compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell collapsing />
              <Table.HeaderCell>
                <HeaderLabel>
                  {translationTools[language].ETF_AGENT.TABLE_TICKER}
                  <Icon
                    name="sort"
                    style={{ cursor: 'pointer' }}
                    onClick={() => this.setSort('ticker')}
                  />
                </HeaderLabel>
              </Table.HeaderCell>
              <Table.HeaderCell>
                <HeaderLabel>
                  {translationTools[language].ETF_AGENT.TABLE_FUND_NAME}
                  <Icon
                    name="sort"
                    style={{ cursor: 'pointer' }}
                    onClick={() => this.setSort('long_name')}
                  />
                </HeaderLabel>
              </Table.HeaderCell>
              <Table.HeaderCell>
                <HeaderLabel>
                  {translationTools[language].ETF_AGENT.TABLE_EXCHANGE}
                  <Icon
                    name="sort"
                    style={{ cursor: 'pointer' }}
                    onClick={() => this.setSort('exchange_symbol')}
                  />
                </HeaderLabel>
              </Table.HeaderCell>
              <Table.HeaderCell>
                <HeaderLabel>
                  {translationTools[language].ETF_AGENT.TABLE_ASSET_CLASS}
                  <Icon
                    name="sort"
                    style={{ cursor: 'pointer' }}
                    onClick={() => this.setSort('global_asset_class')}
                  />
                </HeaderLabel>
              </Table.HeaderCell>
              <Table.HeaderCell>
                <HeaderLabel>
                  {translationTools[language].ETF_AGENT.TABLE_AUM}
                  <Icon
                    name="sort"
                    style={{ cursor: 'pointer' }}
                    onClick={() => this.setSort('aum')}
                  />
                </HeaderLabel>
              </Table.HeaderCell>
              <Table.HeaderCell>
                <HeaderLabel>
                  {translationTools[language].ETF_AGENT.TABLE_MER}
                  <Icon
                    name="sort"
                    style={{ cursor: 'pointer' }}
                    onClick={() => this.setSort('mer')}
                  />
                </HeaderLabel>
              </Table.HeaderCell>
              <Table.HeaderCell>
                <HeaderLabel>
                  {translationTools[language].ETF_AGENT.TABLE_YIELD}
                  <Icon
                    name="sort"
                    style={{ cursor: 'pointer' }}
                    onClick={() => this.setSort('_yield')}
                  />
                </HeaderLabel>
              </Table.HeaderCell>
              <Table.HeaderCell>
                <HeaderLabel>
                  {translationTools[language].ETF_AGENT.TABLE_VOLUME}
                  <Icon
                    name="sort"
                    style={{ cursor: 'pointer' }}
                    onClick={() => this.setSort('volume')}
                  />
                </HeaderLabel>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {this.sortResults(
              sortBy.property,
              sortBy.ascending,
              categoryResults
            ).map((match) => (
              <ResultsTableRow
                key={match.ticker}
                selected={selectedTickers.includes(match.ticker)}
                onClick={() => this.toggleTicker(match.ticker)}
              >
                <Table.Cell collapsing>
                  <Checkbox
                    checked={selectedTickers.includes(match.ticker)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => this.toggleTicker(match.ticker)}
                  />
                </Table.Cell>
                <Table.Cell>
                  <div>{match.ticker || '-'}</div>
                </Table.Cell>
                <Table.Cell>
                  <div>{match.long_name || '-'}</div>
                </Table.Cell>
                <Table.Cell>
                  <div>{match.exchange_symbol || '-'}</div>
                </Table.Cell>
                <Table.Cell>
                  <div>{match.global_asset_class || '-'}</div>
                </Table.Cell>
                <Table.Cell>
                  <div>{this.formatAum(match.aum)}</div>
                </Table.Cell>
                <Table.Cell>
                  <div>{match.mer ? `${match.mer}%` : '-'}</div>
                </Table.Cell>
                <Table.Cell>
                  <div>
                    {match._yield ? `${match._yield.toFixed(2)}%` : '-'}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <div>
                    {match.volume ? match.volume.toLocaleString() : '-'}
                  </div>
                </Table.Cell>
              </ResultsTableRow>
            ))}
          </Table.Body>
        </Table>
      </div>
    );
  };

  render() {
    const { etfData, user_region, french } = this.props;
    const language = french ? 'fr' : 'en';
    const t = translationTools[language].ETF_AGENT;
    const { hasSearched } = this.state;
    const { isFetching, categoryResults, selectedTickers, isByCategory } =
      etfData;

    const queries = [
      t.CATEGORY_QUERY_1,
      t.CATEGORY_QUERY_2,
      t.CATEGORY_QUERY_3,
      t.CATEGORY_QUERY_4,
    ];

    const showChartingToolContent = Boolean(
      isFetching ||
        hasSearched ||
        (categoryResults && categoryResults.length > 0)
    );

    return (
      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          height: '100%',
        }}
      >
        <HeaderContainer
          showChartingToolContent={showChartingToolContent}
          scrollYPos={window.scrollY}
        >
          <Header
            size="large"
            textAlign="center"
            style={{ marginBottom: '1rem' }}
          >
            <Header.Content>{t.TITLE}</Header.Content>
            <Header.Subheader>
              {user_region === 'CA' ? t.SUBTITLE_CA : t.SUBTITLE_US}
            </Header.Subheader>
            <Header.Subheader>{t.AS_OF_DATE}</Header.Subheader>
          </Header>
          <ModeToggle>
            <Mode
              onClick={() => this.setIsByCategory(true)}
              isActive={isByCategory}
            >
              {t.MODE_CATEGORY}
            </Mode>
            <Mode
              onClick={() => this.setIsByCategory(false)}
              isActive={!isByCategory}
            >
              {t.MODE_SIMILARITY}
            </Mode>
          </ModeToggle>
          <Header
            size="large"
            textAlign="center"
            style={{ marginBottom: '1rem' }}
          >
            <Header.Subheader>{t.CATEGORY_SUBTITLE}</Header.Subheader>
          </Header>
          <QueryRow>
            {queries.map((query) => (
              <Query key={query} onClick={() => this.handleQueryClick(query)}>
                {query}
              </Query>
            ))}
          </QueryRow>
          <SearchContainer>
            <SearchInputWrapper>
              <SearchInput
                value={this.state.searchInput}
                onChange={this.handleSearchChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    this.handleSearch();
                  }
                }}
                placeholder={t.CATEGORY_SEARCH_PLACEHOLDER}
              />
              <SearchIcon name="search" />
            </SearchInputWrapper>
            <ButtonFilled
              color={colors.blue}
              onClick={() => this.handleSearch()}
              disabled={isFetching}
              style={{
                width: '123px',
                marginLeft: '20px',
              }}
              className={isFetching ? 'disabled' : ''}
            >
              {t.FIND}
            </ButtonFilled>
          </SearchContainer>
        </HeaderContainer>

        {showChartingToolContent && (
          <Fade duration={3500}>
            <ContentContainer>
              {isFetching && (
                <LogoSpinner
                  style={{ height: '50%', width: '96%', position: 'inherit' }}
                />
              )}
              {!isFetching &&
                hasSearched &&
                categoryResults &&
                categoryResults.length === 0 && (
                  <p style={{ color: 'red', margin: 'auto' }}>
                    {t.NO_RESULTS_CATEGORY}
                  </p>
                )}
              {!isFetching && categoryResults && categoryResults.length > 0 && (
                <div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 'bold',
                        color: colors.navyBlue,
                        fontSize: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {categoryResults.length}{' '}
                      {categoryResults.length === 1
                        ? t.ETF_FOUND_SINGULAR
                        : t.ETF_FOUND_PLURAL}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <ButtonFilled
                        color={colors.blue}
                        onClick={this.goToFundComparison}
                        style={{
                          width: '137px',
                        }}
                        disabled={selectedTickers.length === 0}
                        className={
                          selectedTickers.length === 0 ? 'disabled' : ''
                        }
                      >
                        {t.FUND_DETAILS}
                      </ButtonFilled>
                      <p
                        style={{
                          color: 'rgba(0,0,0,.6)',
                          fontSize: '14px',
                          marginTop: '0.5rem',
                        }}
                      >
                        {selectedTickers.length} {t.SELECTED_PREFIX}{' '}
                        {4 - selectedTickers.length} {t.SELECTED_SUFFIX}
                      </p>
                    </div>
                  </div>
                  {this.renderResultsTable()}
                </div>
              )}
            </ContentContainer>
          </Fade>
        )}
      </div>
    );
  }
}

Category.propTypes = {
  dispatch: PropTypes.func.isRequired,
  user_region: PropTypes.string,
  etfData: PropTypes.object,
  history: PropTypes.object.isRequired,
  french: PropTypes.bool,
};

Category.defaultProps = {
  user_region: 'CA',
  etfData: {},
  french: false,
};

export default withRouter(
  connect((state) => ({
    user_region: localStorage.getItem('user_region') || 'CA',
    etfData: state.ETFAgent,
    french: state.Storage.language === 'fr',
  }))(Category)
);

const HeaderContainer = styled(({ className, children, ...rest }) => (
  <div className={className} {...rest}>
    {children}
  </div>
))`
  position: absolute;
  left: 50%;
  transform: ${(props) =>
    !props.showChartingToolContent
      ? 'translate(-50%, 135px)'
      : 'translate(-50%, 0)'};
  transition: transform 1s;
  background-color: white;
  z-index: 2;
  width: 100%;
`;

const ContentContainer = styled.div`
  padding-top: 320px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  width: 80%;
`;

const ResultsTableRow = styled(Table.Row)`
  position: relative;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? '#f9fafb' : 'inherit')};

  :hover {
    background-color: #f9fafb;
  }
`;

const HeaderLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
  white-space: nowrap;
`;

const QueryRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  margin: 1rem;
`;

const Query = styled.div`
  color: ${colors.navyBlue};
  border: 1px solid #e4e7ec;
  background-color: #f9fafb;
  padding: 1rem;
  border-radius: 500rem;
  cursor: pointer;
  :hover {
    background-color: #f0f0f0;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: auto;
  width: 70%;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 48px;
  border-radius: 500rem;
  border: 2px solid #e4e7ec;
  padding: 0 60px 0 30px;
  font-size: 16px;
  color: #6b7a99;

  ::placeholder {
    color: #a7b3c9;
  }

  :focus {
    outline: none;
    border-color: #7185ff;
  }
`;

const SearchIcon = styled(Icon)`
  position: absolute;
  right: 25px;
  top: 42%;
  transform: translateY(-50%);
  color: #667085;
  font-size: 22px;
`;

const ModeToggle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const Mode = styled.div`
  font-size: 16px;
  color: ${(props) => (props.isActive ? colors.blue : 'black')};
  font-weight: ${(props) => (props.isActive ? 'bold' : 'normal')};
  cursor: pointer;
  border-bottom: ${(props) =>
    props.isActive ? `3px solid ${colors.blue}` : 'none'};
  padding: 0.5rem;
  margin: 1rem;
`;
